import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AlertInfo, AlertService, AlertType } from '../Alert/alert.service';
import { BaseService } from '../base.service';

@Injectable({
  providedIn: 'root',
})
export class BuildingService extends BaseService {
  constructor(
    private http: HttpClient,
    alertService: AlertService
  ) {
    super('building', alertService);
  }

  getBuildings(pageNumber = 0, pageSize = 65535, excludeClosures = false): Observable<any> {
    const url = this.url;
    var params = new HttpParams();
    return this.http
      .get(url, {
        params: new HttpParams()
          .set('page', pageNumber.toString())
          .set('pageSize', pageSize.toString())
          .set('excludeClosures', excludeClosures.toString()),
      })
      .pipe(
        map((data) => data),
        catchError((err) => this.handleError('Error Getting Buildings', err))
      );
  }

  getBuildingsByFilter(
    pageNumber = 0,
    pageSize = 65535,
    excludeClosures = false,
    buildingFilter = '',
    sortOrder = 'asc',
    sortColumn = ''
  ): Observable<any> {
    const url = this.url;
    var params = new HttpParams();

    if (pageNumber >= 0) params = params.set('page', pageNumber.toString());

    if (pageSize >= 0) params = params.set('pageSize', pageSize.toString());

    params = params.set('excludeClosures', excludeClosures.toString());

    if (buildingFilter) params = params.set('buildingFilter', buildingFilter.toString());

    if (sortOrder) params = params.set('sortOrder', sortOrder.toString());

    if (sortColumn) params = params.set('sortColumn', sortColumn.toString());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Buildings', err))
    );
  }

  getBuildingsByLoggedUser(userId: number, pageNumber = 0, pageSize = 65535): Observable<any> {
    const url = this.url + '/me/' + userId;
    return this.http
      .get(url, {
        params: new HttpParams()
          .set('page', pageNumber.toString())
          .set('pageSize', pageSize.toString()),
      })
      .pipe(
        map((data) => data),
        catchError((err) => this.handleError('Error Getting Buildings', err))
      );
  }

  getBuildingsBySelectedUser(userId: number, pageNumber = 0, pageSize = 65535): Observable<any> {
    const url = this.url + '/users/' + userId;
    return this.http
      .get(url, {
        params: new HttpParams()
          .set('page', pageNumber.toString())
          .set('pageSize', pageSize.toString()),
      })
      .pipe(
        map((data) => data),
        catchError((err) => this.handleError('Error Getting Buildings', err))
      );
  }

  getBuilding(id: number): Observable<any> {
    const url = `${this.url}/${id}`;
    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Buildings', err))
    );
  }

  create(body: any): Observable<any> {
    return this.http.post(this.url, body).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Creating Building', err))
    );
  }

  update(id: number, body: any): Observable<any> {
    const updateUrl = this.url + '/' + id;

    return this.http.patch(updateUrl, body).pipe(
      map((innerResponse) => innerResponse),
      catchError((err) => this.handleError('Error updating building', err))
    );
  }

  delete(id: number): Observable<any> {
    const url = this.url + '/' + id;
    return this.http.delete(url).pipe(
      map((response) => response),
      catchError((err) => this.handleError('Error deleting building', err))
    );
  }

  getFloors(buildingId: number, excludeClosures = false): Observable<any> {
    const url = this.url + '/' + buildingId + '/floors';

    var params = new HttpParams();
    params = params.set('excludeClosures', excludeClosures.toString());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Floors For Building', err))
    );
  }

  getFloorsbyUser(buildingId: number, userId: number): Observable<any> {
    const url = this.url + '/' + buildingId + '/' + userId + '/floorsByUser';

    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Floors For Building', err))
    );
  }

  // Get floors that this user is a division administrator
  getFloorsbyUserDivision(buildingId: number, userId: number): Observable<any> {
    const url = this.url + '/' + buildingId + '/' + userId + '/floorsByUserDivision';

    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Floors For Building', err))
    );
  }

  getPagedFloors(buildingId: number, pageNumber: number, pageSize: number): Observable<any> {
    const url = this.url + '/' + buildingId + '/floors';

    var params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Floors For Building', err))
    );
  }

  downloadProcedure(id: number): Observable<any> {
    const url = this.url + '/download/procedure/' + id;
    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Downloading Procedure', err))
    );
  }

  disable(schedule: any): Observable<any> {
    const url = this.url + '/disable';
    return this.http.post(url, schedule).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Disabling Building', err))
    );
  }

  enable(id: number): Observable<any> {
    const url = this.url + '/enable/' + id;
    return this.http.post(url, null).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Enabling Building', err))
    );
  }

  getBuildingsByNameAddress(
    pageNumber = 0,
    pageSize = 65535,
    searchString = '',
    excludeClosures = false
  ): Observable<any> {
    const url = this.url;
    var params = new HttpParams();
    return this.http
      .get(url, {
        params: new HttpParams()
          .set('page', pageNumber.toString())
          .set('pageSize', pageSize.toString())
          .set('excludeClosures', excludeClosures.toString()),
      })
      .pipe(
        map((data) => {
          console.log(data);
          return data;
        }),
        catchError((err) => this.handleError('Error Getting Buildings', err))
      );
  }
}
